import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Routes from '../configuration/routes/RouteList';
import getApiUrl from '../configuration/getApiUrl';
import getCookie from '../functions/getCookie';
import {
  appendNoteVersion,
  deletePersistedNote,
  ensureNoteVersions,
  getCachedNote,
  getPersistedNote,
  loadNoteSplitRatio,
  removeCachedNote,
  saveNoteSplitRatio,
  updatePersistedNote,
  upsertCachedNote,
  type PersistedNoteVersion,
  type PersistedTranscriptionNote,
} from '../services/persistedNotes';

type ProviderMode = 'cloud' | 'local';
type TranscriptionNote = PersistedTranscriptionNote;

const COMPACT_LAYOUT_BREAKPOINT = 1100;

function formatDuration(ms?: number) {
  if (!ms) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function countWords(value: string) {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

function truncateText(value?: string, maxLength = 90) {
  if (!value) return '';
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}...`;
}

function ProviderBadge({ mode, cloud }: { mode: ProviderMode; cloud?: string }) {
  const label = mode === 'local' ? 'LOCAL' : (cloud || 'cloud').toUpperCase();
  const bg =
    mode === 'local'
      ? '#4caf50'
      : cloud === 'groq'
        ? '#ff9800'
        : cloud === 'mistral'
          ? '#6c47ff'
          : '#1a73e8';
  return (
    <span
      style={{
        background: bg,
        color: '#fff',
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '999px',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </span>
  );
}

const ENHANCE_PROMPTS = [
  {
    id: 'summarize',
    name: 'Summary',
    prompt:
      'Create a clear, concise summary of this transcript. Focus on the main topics discussed and key takeaways. Use bullet points for readability.',
  },
  {
    id: 'action-items',
    name: 'Action Items',
    prompt:
      'Extract all action items, tasks, and follow-ups from this transcript. For each item, specify who is responsible (if mentioned) and any deadlines.',
  },
  {
    id: 'clean-up',
    name: 'Clean Up',
    prompt:
      'Clean up this transcript. Remove filler words (um, uh, like), correct obvious transcription errors, and add proper punctuation and paragraph breaks while preserving the original meaning and tone.',
  },
  {
    id: 'format-meeting',
    name: 'Meeting Minutes',
    prompt:
      'Format this transcript into professional meeting minutes. Include:\n1. Meeting Objective\n2. Key Discussion Points\n3. Decisions Made\n4. Action Items',
  },
];

const REASONING_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI',
    models: [
      { id: 'gpt-4o', name: 'GPT-4o' },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
      { id: 'gpt-5.4', name: 'GPT-5.4 (Reasoning)' },
    ],
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    models: [
      { id: 'claude-3-7-sonnet', name: 'Claude 3.7 Sonnet' },
      { id: 'claude-3-5-haiku', name: 'Claude 3.5 Haiku' },
    ],
  },
  {
    id: 'groq',
    name: 'Groq',
    models: [
      { id: 'llama-3.3-70b-versatile', name: 'LLaMA 3.3 70B' },
      { id: 'deepseek-r1-distill-llama-70b', name: 'DeepSeek R1 (70B)' },
      { id: 'qwen-2.5-32b', name: 'Qwen 2.5 32B' },
    ],
  },
  {
    id: 'google',
    name: 'Google Gemini',
    models: [
      { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash (Preview)' },
      { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro (Preview)' },
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
      { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite' },
      { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
    ],
  },
  {
    id: 'local',
    name: 'Local AI',
    models: [
      { id: 'qwen3-8b-q4_k_m', name: 'Qwen3 8B' },
      { id: 'mistral-7b-instruct-v0.3', name: 'Mistral 7B' },
      { id: 'llama-3.2-3b-instruct', name: 'Llama 3.2 3B' },
    ],
  },
];

const TranscriptionNote = () => {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();

  const [note, setNote] = useState<TranscriptionNote | null>(null);
  const [versions, setVersions] = useState<PersistedNoteVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [enhanceProvider, setEnhanceProvider] = useState(REASONING_PROVIDERS[0].id);
  const [enhanceModel, setEnhanceModel] = useState(REASONING_PROVIDERS[0].models[0].id);
  const [enhanceApiKey, setEnhanceApiKey] = useState('');
  const [enhancePromptId, setEnhancePromptId] = useState(ENHANCE_PROMPTS[0].id);
  const [customPrompt, setCustomPrompt] = useState(ENHANCE_PROMPTS[0].prompt);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');
  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < COMPACT_LAYOUT_BREAKPOINT : false,
  );
  const [splitRatio, setSplitRatio] = useState(() => loadNoteSplitRatio());
  const [isResizing, setIsResizing] = useState(false);

  const splitPaneRef = useRef<HTMLDivElement | null>(null);

  const selectedVersion = useMemo(
    () => versions.find(version => version.id === selectedVersionId) ?? null,
    [selectedVersionId, versions],
  );
  const activeReasoningProvider =
    REASONING_PROVIDERS.find(provider => provider.id === enhanceProvider) ?? REASONING_PROVIDERS[0];
  const currentPreset =
    ENHANCE_PROMPTS.find(prompt => prompt.id === enhancePromptId) ?? ENHANCE_PROMPTS[0];
  const savedVersion =
    note
      ? [...versions].reverse().find(version => version.content === note.text) ?? null
      : null;
  const hasUnsavedTextChanges = selectedVersion ? selectedVersion.content !== text : false;
  const hasUnsavedTitleChanges = note ? note.title !== title : false;
  const hasUnsavedChanges = hasUnsavedTextChanges || hasUnsavedTitleChanges;

  useEffect(() => {
    const handleResize = () => {
      setIsCompactLayout(window.innerWidth < COMPACT_LAYOUT_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    saveNoteSplitRatio(splitRatio);
  }, [splitRatio]);

  useEffect(() => {
    if (!isResizing || isCompactLayout) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const rect = splitPaneRef.current?.getBoundingClientRect();
      if (!rect) return;
      const ratio = (event.clientX - rect.left) / rect.width;
      setSplitRatio(Math.min(0.75, Math.max(0.35, ratio)));
    };

    const stopResizing = () => {
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
    };
  }, [isCompactLayout, isResizing]);

  useEffect(() => {
    const firstModel = activeReasoningProvider.models[0]?.id;
    if (firstModel && !activeReasoningProvider.models.some(model => model.id === enhanceModel)) {
      setEnhanceModel(firstModel);
    }
  }, [activeReasoningProvider, enhanceModel]);

  useEffect(() => {
    if (!noteId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const hydrateNote = (loadedNote: TranscriptionNote, errorMessage = '') => {
      const versionHistory = ensureNoteVersions(loadedNote);
      const matchedVersion =
        [...versionHistory].reverse().find(version => version.content === loadedNote.text) ??
        versionHistory[versionHistory.length - 1] ??
        null;

      setNote(loadedNote);
      setVersions(versionHistory);
      setSelectedVersionId(matchedVersion?.id ?? '');
      setTitle(loadedNote.title);
      setText(loadedNote.text);
      setWordCount(countWords(loadedNote.text));
      setLoadError(errorMessage);
    };

    const loadNote = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const found = await getPersistedNote(noteId);
        if (cancelled) return;
        hydrateNote(found);
      } catch (error) {
        if (cancelled) return;

        const cached = getCachedNote(noteId) || null;
        if (cached) {
          hydrateNote(
            cached,
            'This note could not be refreshed from the server, so a cached copy is shown.',
          );
        } else {
          setNote(null);
          setVersions([]);
          setSelectedVersionId('');
          setLoadError(error instanceof Error ? error.message : 'Failed to load note');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadNote();

    return () => {
      cancelled = true;
    };
  }, [noteId]);

  useEffect(() => {
    setWordCount(countWords(text));
    setSaved(false);
  }, [text, title]);

  const handleSave = async () => {
    if (!note) return;

    try {
      const updated = await updatePersistedNote(note.id, { title, text });
      const mergedNote: TranscriptionNote = {
        ...updated,
        enhanced_content: note.enhanced_content,
        enhancement_prompt: note.enhancement_prompt,
      };
      const snapshot = appendNoteVersion(updated.id, {
        content: updated.text,
        prompt: 'Saved to note',
        source: 'manual',
      });

      setNote(mergedNote);
      setVersions(snapshot.versions);
      setSelectedVersionId(snapshot.activeVersion.id);
      setTitle(updated.title);
      setText(updated.text);
      upsertCachedNote(mergedNote);
      setSaved(true);
      setLoadError('');
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to save note');
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!note) return;
    if (!confirm(`Delete note "${note.title}"?`)) return;

    try {
      await deletePersistedNote(note.id);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to delete note');
      return;
    }

    removeCachedNote(note.id);
    navigate(Routes.Notes);
  };

  const handleExport = () => {
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || note?.title || 'Niuniu_Note'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCreateSnapshot = () => {
    if (!note) return;
    const snapshot = appendNoteVersion(note.id, {
      content: text,
      prompt: customPrompt.trim() || 'Manual snapshot',
      source: 'manual',
    });
    setVersions(snapshot.versions);
    setSelectedVersionId(snapshot.activeVersion.id);
  };

  const handleRestoreSaved = () => {
    if (!note) return;

    setTitle(note.title);
    setText(note.text);
    if (savedVersion) {
      setSelectedVersionId(savedVersion.id);
    }
    setEnhanceError('');
  };

  const handleLoadVersion = (version: PersistedNoteVersion) => {
    setText(version.content);
    setSelectedVersionId(version.id);
    setEnhanceError('');
  };

  const handleEnhance = async () => {
    if (!note) return;

    setIsEnhancing(true);
    setEnhanceError('');

    const systemPrompt = customPrompt.trim() || currentPreset.prompt;

    try {
      const csrf = getCookie('csrftoken');
      const resp = await fetch(`${getApiUrl()}/api/download/enhance/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf || '',
        },
        body: JSON.stringify({
          provider: enhanceProvider,
          model: enhanceModel,
          api_key: enhanceApiKey,
          system_prompt: systemPrompt,
          text,
        }),
      });
      const data = (await resp.json()) as { error?: string; text?: string };

      if (!resp.ok) {
        setEnhanceError(data.error || 'Enhancement failed');
        return;
      }

      if (!data.text) {
        setEnhanceError('Enhancement returned no text');
        return;
      }

      const snapshot = appendNoteVersion(note.id, {
        content: data.text,
        prompt: systemPrompt,
        source: 'ai',
      });
      const updatedNote: TranscriptionNote = {
        ...note,
        enhanced_content: data.text,
        enhancement_prompt: systemPrompt,
      };

      upsertCachedNote(updatedNote);
      setNote(updatedNote);
      setVersions(snapshot.versions);
      setSelectedVersionId(snapshot.activeVersion.id);
      setText(snapshot.activeVersion.content);
    } catch (error) {
      setEnhanceError(error instanceof Error ? error.message : 'Network error');
    } finally {
      setIsEnhancing(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <title>NM | Loading note</title>
        <div className="boxed-content">
          <div className="title-bar">
            <h1>Loading note...</h1>
          </div>
          <p>
            <Link to={Routes.Notes}>← Back to Notes</Link>
          </p>
        </div>
      </>
    );
  }

  if (!note) {
    return (
      <>
        <title>NM | Note not found</title>
        <div className="boxed-content">
          <div className="title-bar">
            <h1>Note not found</h1>
          </div>
          {loadError && <p style={{ color: '#f44336' }}>{loadError}</p>}
          <p>
            <Link to={Routes.Notes}>← Back to Notes</Link>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <title>NM | {title || note.title}</title>
      <div className="boxed-content">
        {loadError && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 14px',
              border: '1px solid #ff9800',
              borderRadius: '8px',
              color: '#ff9800',
              fontSize: '13px',
            }}
          >
            {loadError}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 420px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              <Link to={Routes.Notes} style={{ fontSize: '13px', opacity: 0.65 }}>
                ← Back to Notes
              </Link>
              <ProviderBadge mode={note.providerMode} cloud={note.cloudProvider} />
              {selectedVersion && (
                <span style={{ fontSize: '12px', opacity: 0.55 }}>
                  Viewing {selectedVersion.label}
                </span>
              )}
              {savedVersion && (
                <span style={{ fontSize: '12px', opacity: 0.45 }}>
                  Saved {savedVersion.label}
                </span>
              )}
            </div>

            <input
              type="text"
              value={title}
              onChange={event => setTitle(event.target.value)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '30px',
                fontWeight: 700,
                color: 'inherit',
                padding: 0,
              }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.55, lineHeight: 1.6 }}>
              左边是可编辑正文，右边是 prompt 和版本区。每次 AI 优化或手动快照都会生成一个版本，你可以随时切回去继续改。
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="button-primary" onClick={handleExport}>
              ⬇ Export .md
            </button>
            <button className="button-primary" onClick={handleCopy}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
            <button className="button-primary" onClick={handleCreateSnapshot}>
              Snapshot
            </button>
            <button className="button-primary" onClick={handleSave} disabled={saved}>
              {saved ? '✓ Saved' : 'Save'}
            </button>
            <button className="button-delete" onClick={handleDelete}>
              Delete
            </button>
          </div>
        </div>

        <div className="info-box" style={{ marginBottom: '20px' }}>
          <div className="info-box-item" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <span>
              <strong>File:</strong> {note.fileName}
            </span>
            {note.audioDurationSec && (
              <span>
                <strong>Duration:</strong> {formatDuration(note.audioDurationSec * 1000)}
              </span>
            )}
            <span>
              <strong>Model:</strong> {note.model}
            </span>
            <span>
              <strong>Words:</strong> {wordCount.toLocaleString()}
            </span>
            <span>
              <strong>Created:</strong> {formatDate(note.createdAt)}
            </span>
          </div>
        </div>

        <div
          ref={splitPaneRef}
          style={{
            display: 'flex',
            flexDirection: isCompactLayout ? 'column' : 'row',
            border: '1px solid var(--border-color, #2a3a4e)',
            borderRadius: '16px',
            overflow: 'hidden',
            minHeight: isCompactLayout ? 'auto' : '700px',
            background:
              'linear-gradient(180deg, rgba(7, 19, 34, 0.96) 0%, rgba(10, 24, 42, 0.96) 100%)',
            boxShadow: '0 18px 38px rgba(0, 0, 0, 0.22)',
          }}
        >
          <div
            style={{
              flex: isCompactLayout ? '1 1 auto' : `0 0 ${splitRatio * 100}%`,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: '18px',
              gap: '14px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '16px' }}>Transcript Workspace</h2>
                <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.55 }}>
                  {selectedVersion
                    ? `Current branch: ${selectedVersion.label}`
                    : 'Start editing from the saved transcript'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {hasUnsavedChanges ? (
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '5px 10px',
                      borderRadius: '999px',
                      background: 'rgba(255, 193, 7, 0.14)',
                      color: '#ffc107',
                    }}
                  >
                    Unsaved changes
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '5px 10px',
                      borderRadius: '999px',
                      background: 'rgba(76, 175, 80, 0.14)',
                      color: '#7bd67f',
                    }}
                  >
                    Synced with current version
                  </span>
                )}
              </div>
            </div>

            <textarea
              value={text}
              onChange={event => setText(event.target.value)}
              rows={28}
              style={{
                flex: 1,
                minHeight: isCompactLayout ? '420px' : '560px',
                width: '100%',
                fontFamily:
                  'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
                fontSize: '14px',
                lineHeight: 1.8,
                padding: '18px',
                borderRadius: '12px',
                resize: 'none',
                background: 'rgba(5, 15, 29, 0.9)',
                color: 'inherit',
                border: '1px solid var(--border-color, #30435a)',
                boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', fontSize: '12px', opacity: 0.55 }}>
              <span>
                {wordCount.toLocaleString()} words · {text.length.toLocaleString()} chars
              </span>
              <span>
                {selectedVersion
                  ? `Loaded from ${selectedVersion.label}${hasUnsavedTextChanges ? ' and edited further' : ''}`
                  : 'No version selected'}
              </span>
            </div>
          </div>

          {!isCompactLayout && (
            <div
              onPointerDown={event => {
                event.preventDefault();
                setIsResizing(true);
              }}
              style={{
                position: 'relative',
                width: '14px',
                cursor: 'col-resize',
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.02) 100%)',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '4px',
                  height: '84px',
                  borderRadius: '999px',
                  background: 'rgba(255, 255, 255, 0.18)',
                }}
              />
            </div>
          )}

          <div
            style={{
              flex: '1 1 0',
              minWidth: 0,
              padding: '18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              borderTop: isCompactLayout ? '1px solid var(--border-color, #2a3a4e)' : 'none',
              background:
                'linear-gradient(180deg, rgba(12, 30, 50, 0.98) 0%, rgba(8, 20, 34, 0.98) 100%)',
            }}
          >
            <div
              style={{
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #30435a)',
                background: 'rgba(255, 255, 255, 0.03)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '16px' }}>Prompt Studio</h2>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.55 }}>
                    在右边写 prompt，左边正文会直接接收新版本结果。你也可以只做快照，不马上覆盖数据库。
                  </p>
                </div>
                <button
                  className="button-primary"
                  onClick={handleRestoreSaved}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Restore Saved
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {ENHANCE_PROMPTS.map(prompt => (
                  <button
                    key={prompt.id}
                    onClick={() => {
                      setEnhancePromptId(prompt.id);
                      setCustomPrompt(prompt.prompt);
                    }}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      border: `1px solid ${
                        enhancePromptId === prompt.id
                          ? 'var(--highlight-bg, #1a73e8)'
                          : 'var(--border-color, #44566d)'
                      }`,
                      background:
                        enhancePromptId === prompt.id
                          ? 'rgba(26, 115, 232, 0.18)'
                          : 'rgba(255, 255, 255, 0.02)',
                      color: 'inherit',
                    }}
                  >
                    {prompt.name}
                  </button>
                ))}
              </div>

              <textarea
                value={customPrompt}
                onChange={event => setCustomPrompt(event.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color, #44566d)',
                  background: 'rgba(5, 15, 29, 0.88)',
                  color: 'inherit',
                  padding: '14px',
                  lineHeight: 1.6,
                  marginBottom: '14px',
                  resize: 'vertical',
                }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: isCompactLayout ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', opacity: 0.5, marginBottom: '4px' }}>
                    PROVIDER
                  </label>
                  <select
                    value={enhanceProvider}
                    onChange={event => {
                      setEnhanceProvider(event.target.value);
                      const provider = REASONING_PROVIDERS.find(item => item.id === event.target.value);
                      if (provider?.models[0]) {
                        setEnhanceModel(provider.models[0].id);
                      }
                    }}
                    style={{ width: '100%' }}
                  >
                    {REASONING_PROVIDERS.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', opacity: 0.5, marginBottom: '4px' }}>
                    MODEL
                  </label>
                  <select
                    value={enhanceModel}
                    onChange={event => setEnhanceModel(event.target.value)}
                    style={{ width: '100%' }}
                  >
                    {activeReasoningProvider.models.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {enhanceProvider !== 'local' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', opacity: 0.5, marginBottom: '4px' }}>
                    API KEY
                  </label>
                  <input
                    type="password"
                    value={enhanceApiKey}
                    onChange={event => setEnhanceApiKey(event.target.value)}
                    placeholder="sk-..."
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {enhanceError && (
                <div
                  style={{
                    color: '#f44336',
                    fontSize: '12px',
                    marginBottom: '12px',
                    padding: '8px 10px',
                    border: '1px solid #f44336',
                    borderRadius: '6px',
                  }}
                >
                  {enhanceError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button className="button-primary" onClick={handleEnhance} disabled={isEnhancing}>
                  {isEnhancing ? 'Optimizing...' : 'Apply AI Optimization'}
                </button>
                <button className="button-primary" onClick={handleCreateSnapshot}>
                  Snapshot Current Text
                </button>
                <button className="button-primary" onClick={handleSave} disabled={saved}>
                  {saved ? 'Saved to DB' : 'Save Current Text'}
                </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #30435a)',
                background: 'rgba(255, 255, 255, 0.03)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '16px' }}>Version Stack</h2>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.55 }}>
                    每个版本都会留在这里。点任意一条，左边正文会切到那个版本继续编辑。
                  </p>
                </div>
                <span style={{ fontSize: '12px', opacity: 0.55 }}>
                  {versions.length} versions
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', paddingRight: '4px' }}>
                {[...versions].reverse().map((version, reverseIndex) => {
                  const versionIndex = versions.length - 1 - reverseIndex;
                  const previousVersion = versionIndex > 0 ? versions[versionIndex - 1] : null;
                  const delta = countWords(version.content) - countWords(previousVersion?.content || '');
                  const isActive = version.id === selectedVersionId;
                  const sourceLabel =
                    version.source === 'ai'
                      ? 'AI'
                      : version.source === 'manual'
                        ? 'Manual'
                        : 'Original';

                  return (
                    <button
                      key={version.id}
                      onClick={() => handleLoadVersion(version)}
                      style={{
                        textAlign: 'left',
                        padding: '14px',
                        borderRadius: '12px',
                        border: `1px solid ${
                          isActive
                            ? 'var(--highlight-bg, #1a73e8)'
                            : 'var(--border-color, #44566d)'
                        }`,
                        background: isActive ? 'rgba(26, 115, 232, 0.15)' : 'rgba(6, 16, 29, 0.7)',
                        color: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <strong>{version.label}</strong>
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '4px 8px',
                              borderRadius: '999px',
                              background:
                                version.source === 'ai'
                                  ? 'rgba(33, 150, 243, 0.16)'
                                  : version.source === 'manual'
                                    ? 'rgba(255, 193, 7, 0.14)'
                                    : 'rgba(76, 175, 80, 0.14)',
                              color:
                                version.source === 'ai'
                                  ? '#6cb8ff'
                                  : version.source === 'manual'
                                    ? '#ffc107'
                                    : '#7bd67f',
                            }}
                          >
                            {sourceLabel}
                          </span>
                          {version.id === savedVersion?.id && (
                            <span style={{ fontSize: '11px', opacity: 0.65 }}>Saved note</span>
                          )}
                        </div>
                        <span style={{ fontSize: '11px', opacity: 0.45 }}>
                          {formatDate(version.createdAt)}
                        </span>
                      </div>

                      <div style={{ fontSize: '12px', opacity: 0.75, lineHeight: 1.6, marginBottom: '8px' }}>
                        {truncateText(version.prompt || 'No prompt attached')}
                      </div>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '11px', opacity: 0.55 }}>
                        <span>{countWords(version.content).toLocaleString()} words</span>
                        <span>
                          {previousVersion
                            ? `${delta >= 0 ? '+' : ''}${delta.toLocaleString()} words vs ${previousVersion.label}`
                            : 'Baseline version'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TranscriptionNote;
